import React from 'react';
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Autocomplete, Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import { apiSettingsCardSx } from '../../../constants/apiSettingsCardTypography';
import { useGetBusinessProfilesHandler } from '../../../dataHandlers/getBusinessProfilesHandler';
import { useGetConnectorsHandler } from '../../connectors/dataHandlers/getConnectorsHandler';
import { useGetSftpSettingsHandler, useSaveSftpLegacySettingsHandler } from '../dataHandlers/sftpSettingsHandlers';

const LEGACY_BUCKETS = [
  { key: 'INV', documentType: 'marmin.sale.invoice', label: 'Sale invoice (INV)' },
  { key: 'CN', documentType: 'marmin.sale.credit-note', label: 'Sale credit note (CN)' },
  { key: 'SBINV', documentType: 'marmin.purchase.invoice', label: 'Purchase invoice (SBINV)' },
  { key: 'SBCN', documentType: 'marmin.purchase.credit-note', label: 'Purchase credit note (SBCN)' },
];

const emptyConnectors = () => ({ INV: '', CN: '', SBINV: '', SBCN: '' });

const emptyRow = () => ({ tin: '', connectors: emptyConnectors() });

const profileLabel = (profile) => {
  const name = profile?.name || '';
  const tin = profile?.tin || '';
  if (name && tin) {
    return `${name} (${tin})`;
  }

  return tin || name || '';
};

const SftpLegacySection = () => {
  const dispatch = useDispatch();
  const { legacy: savedLegacy, isLoading: settingsLoading } = useGetSftpSettingsHandler();
  const { profiles, isLoading: profilesLoading } = useGetBusinessProfilesHandler({ searchQuery: {}, enabled: true });
  const { connectors, isLoading: connectorsLoading } = useGetConnectorsHandler();
  const { onSubmit, isPending } = useSaveSftpLegacySettingsHandler();

  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    if (settingsLoading) {
      return;
    }
    setRows(
      savedLegacy.length > 0
        ? savedLegacy.map((row) => ({
            tin: row.tin ?? '',
            connectors: { ...emptyConnectors(), ...(row.connectors ?? {}) },
          }))
        : [],
    );
  }, [savedLegacy, settingsLoading]);

  const profilesWithTin = React.useMemo(
    () => profiles.filter((p) => String(p.tin || '').trim().length > 0),
    [profiles],
  );

  const connectorOptions = React.useMemo(() => connectors ?? [], [connectors]);

  const usedTins = React.useMemo(() => new Set(rows.map((r) => r.tin).filter(Boolean)), [rows]);

  const connectorsForBucket = (bucketKey) =>
    connectorOptions.filter((c) => c.document_type === LEGACY_BUCKETS.find((b) => b.key === bucketKey)?.documentType);

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateConnector = (index, bucketKey, connectorKey) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, connectors: { ...row.connectors, [bucketKey]: connectorKey } } : row,
      ),
    );
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRows = () => {
    const tins = new Set();
    for (const row of rows) {
      if (!row.tin) {
        return 'Each row needs a business profile (TIN).';
      }
      if (tins.has(row.tin)) {
        return 'Each TIN can only appear once in legacy mappings.';
      }
      tins.add(row.tin);
      const hasConnector = LEGACY_BUCKETS.some((b) => String(row.connectors?.[b.key] || '').trim());
      if (!hasConnector) {
        return 'Each row needs at least one document-type connector.';
      }
    }

    return null;
  };

  const handleSave = () => {
    const validationError = validateRows();
    if (validationError) {
      dispatch(showSnackbar({ message: validationError, severity: 'error' }));
      return;
    }

    onSubmit(
      rows.map((row) => {
        const connectors = {};
        LEGACY_BUCKETS.forEach((bucket) => {
          const key = String(row.connectors?.[bucket.key] || '').trim();
          if (key) {
            connectors[bucket.key] = key;
          }
        });

        return {
          tin: row.tin.trim(),
          connectors,
        };
      }),
    );
  };

  const validationError = validateRows();
  const isBusy = settingsLoading || profilesLoading || connectorsLoading || isPending;

  return (
    <Stack spacing={1.5} sx={{ mt: 3 }}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <HistoryIcon fontSize="small" color="primary" />
          <Typography sx={apiSettingsCardSx.title}>Legacy connector mapping</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={apiSettingsCardSx.description}>
          For files named <code>TIN_Source_DocType_InvoiceNumber.json</code>, map each seller TIN to a connector
          template for each document type in the file name (<code>INV</code>, <code>CN</code>, <code>SBINV</code>,{' '}
          <code>SBCN</code>).
        </Typography>
      </Box>

      {rows.length === 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No mappings yet. Add a row for each seller TIN used in legacy JSON file names.
          </Typography>
        </Box>
      )}

      <Stack spacing={1.5}>
        {rows.map((row, index) => {
          const profileValue = profilesWithTin.find((p) => p.tin === row.tin) ?? null;

          return (
            <Box key={`legacy-row-${index}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Autocomplete
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  options={profilesWithTin}
                  loading={profilesLoading}
                  disabled={isBusy}
                  value={profileValue}
                  getOptionLabel={profileLabel}
                  isOptionEqualToValue={(a, b) => a?.profile_id === b?.profile_id}
                  getOptionDisabled={(option) => usedTins.has(option.tin) && option.tin !== row.tin}
                  onChange={(_, profile) => updateRow(index, { tin: profile?.tin ?? '' })}
                  renderInput={(params) => <TextField {...params} label="TIN (profile)" placeholder="Select profile" />}
                />
                <IconButton
                  size="small"
                  aria-label="Remove legacy row"
                  disabled={isBusy}
                  onClick={() => removeRow(index)}
                  sx={{ flexShrink: 0 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>

              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
                {LEGACY_BUCKETS.map((bucket) => {
                  const options = connectorsForBucket(bucket.key);
                  const connectorValue = options.find((c) => c.key === row.connectors?.[bucket.key]) ?? null;

                  return (
                    <Box key={`${index}-${bucket.key}`} sx={{ flex: '1 1 220px', minWidth: 200 }}>
                      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
                        {bucket.label}
                      </Typography>
                      <Autocomplete
                        size="small"
                        fullWidth
                        options={options}
                        loading={connectorsLoading}
                        disabled={isBusy}
                        value={connectorValue}
                        getOptionLabel={(c) => c.name || c.key}
                        isOptionEqualToValue={(a, b) => a?.key === b?.key}
                        onChange={(_, connector) => updateConnector(index, bucket.key, connector?.key ?? '')}
                        renderInput={(params) => <TextField {...params} placeholder="Optional" />}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" startIcon={<AddIcon />} onClick={addRow} disabled={isBusy}>
          Add mapping
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={isBusy || (rows.length > 0 && Boolean(validationError))}
        >
          Save legacy connector mapping
        </Button>
        {rows.length > 0 && validationError && (
          <Typography variant="caption" color="error">
            {validationError}
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};

export default SftpLegacySection;
